import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Trash2, ZoomIn, ZoomOut } from "lucide-react";

/**
 * PhotoLightbox — fullscreen image viewer.
 *
 * Props:
 *  - open: bool
 *  - photos: array of src strings (data URLs or http URLs)
 *  - index: starting index
 *  - onClose: () => void
 *  - onDelete?: (idx) => void   optional delete-from-lightbox callback
 *  - alt?: string               optional alt prefix
 *
 * Behaviour:
 *  - ESC, click on background, X button → close
 *  - Click on image: toggles 1x ↔ 2x zoom
 *  - Wheel (mouse): progressive zoom (1x → 4x)
 *  - When zoomed: drag to pan
 *  - ← / → keys + on-screen arrows: navigate (only when not zoomed for keys)
 *  - Touch: single-finger swipe horizontal to navigate (zoom=1); pinch with two fingers to zoom
 *  - Counter X / N
 */
export default function PhotoLightbox({ open, photos = [], index = 0, onClose, onDelete, alt = "Foto" }) {
  const [i, setI] = useState(index);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const touchRef = useRef({ swipeStartX: null, swipeMoved: false, pinchStartDist: 0, pinchStartZoom: 1 });

  // Reset on open / index change
  useEffect(() => { if (open) setI(index); }, [open, index]);
  useEffect(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, [i]);

  const total = photos.length;
  const safeIndex = Math.min(Math.max(i, 0), Math.max(total - 1, 0));
  const src = photos[safeIndex];

  const next = useCallback(() => { if (total > 1) setI((p) => (p + 1) % total); }, [total]);
  const prev = useCallback(() => { if (total > 1) setI((p) => (p - 1 + total) % total); }, [total]);

  const handleClose = useCallback(() => {
    // Reset zoom/pan so the next open starts clean.
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    onClose?.();
  }, [onClose]);

  // Keyboard
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
      else if (zoom <= 1 && e.key === "ArrowRight") next();
      else if (zoom <= 1 && e.key === "ArrowLeft") prev();
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(4, z + 0.5));
      else if (e.key === "-") setZoom((z) => Math.max(1, z - 0.5));
      else if (e.key === "0") { setZoom(1); setOffset({ x: 0, y: 0 }); }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, next, prev, handleClose, zoom]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.25 : 0.25;
    setZoom((z) => Math.min(4, Math.max(1, z + delta)));
  };

  const toggleZoom = (e) => {
    e.stopPropagation();
    if (zoom > 1) { setZoom(1); setOffset({ x: 0, y: 0 }); }
    else setZoom(2);
  };

  // Drag-to-pan when zoomed
  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, baseX: offset.x, baseY: offset.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.active) return;
    setOffset({ x: dragRef.current.baseX + (e.clientX - dragRef.current.startX),
                y: dragRef.current.baseY + (e.clientY - dragRef.current.startY) });
  };
  const onMouseUp = () => { dragRef.current.active = false; };

  // Touch: pinch + swipe
  const dist = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      touchRef.current.pinchStartDist = dist(e.touches[0], e.touches[1]);
      touchRef.current.pinchStartZoom = zoom;
    } else if (e.touches.length === 1) {
      touchRef.current.swipeStartX = e.touches[0].clientX;
      touchRef.current.swipeMoved = false;
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && touchRef.current.pinchStartDist) {
      const d = dist(e.touches[0], e.touches[1]);
      const ratio = d / touchRef.current.pinchStartDist;
      setZoom(Math.min(4, Math.max(1, touchRef.current.pinchStartZoom * ratio)));
    } else if (e.touches.length === 1 && zoom <= 1 && touchRef.current.swipeStartX != null) {
      const dx = e.touches[0].clientX - touchRef.current.swipeStartX;
      if (Math.abs(dx) > 60) touchRef.current.swipeMoved = true;
    }
  };
  const onTouchEnd = (e) => {
    if (touchRef.current.swipeMoved && zoom <= 1) {
      const dx = (e.changedTouches[0]?.clientX ?? 0) - touchRef.current.swipeStartX;
      if (dx < 0) next(); else prev();
    }
    touchRef.current = { swipeStartX: null, swipeMoved: false, pinchStartDist: 0, pinchStartZoom: 1 };
  };

  const handleBackdropClick = () => handleClose();
  const stop = (e) => e.stopPropagation();

  if (!open || !src) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 select-none"
      onClick={handleBackdropClick}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onWheel={onWheel}
      data-testid="photo-lightbox"
    >
      {/* Image area — drawn FIRST so the controls (rendered after) end up on
          top in DOM order. Plus an explicit z-index just to be defensive on
          browsers that ignore source order under stacking contexts. */}
      <div
        ref={containerRef}
        onClick={(e) => {
          // Backdrop close: only when the click is on the wrapper itself
          // (i.e. the empty margin around the image), NOT bubbling from the
          // image. Clicks on the <img> hit the image's own toggleZoom and
          // are stopPropagation'd there before reaching this handler.
          if (e.target === e.currentTarget) handleClose();
          else stop(e);
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="absolute inset-0 z-10 flex items-center justify-center"
        style={{ cursor: zoom > 1 ? (dragRef.current.active ? "grabbing" : "grab") : "zoom-in" }}
      >
        <img
          src={src}
          alt={`${alt} ${safeIndex + 1}/${total}`}
          loading="lazy"
          draggable={false}
          onClick={toggleZoom}
          onMouseDown={onMouseDown}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transition: dragRef.current.active ? "none" : "transform 180ms ease-out",
          }}
          className="max-w-[92vw] max-h-[88vh] object-contain rounded-sm shadow-2xl"
          data-testid="lightbox-image"
        />
      </div>

      {/* Close — pinned top-right above the image layer (z-50). */}
      <button
        type="button"
        onClick={(e) => { stop(e); handleClose(); }}
        className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur cursor-pointer"
        data-testid="lightbox-close"
        aria-label="Chiudi"
      >
        <X className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Zoom controls — also above the image layer. The wrapper stops
          propagation so a click in the spacing between buttons doesn't
          accidentally reach the backdrop and close the modal. */}
      <div className="absolute top-4 left-4 z-50 flex items-center gap-2" onClick={stop}>
        <button
          type="button"
          onClick={(e) => { stop(e); setZoom((z) => Math.max(1, +(z - 0.5).toFixed(2))); }}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur cursor-pointer"
          aria-label="Zoom out"
          data-testid="lightbox-zoom-out"
        >
          <ZoomOut className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <button
          type="button"
          onClick={(e) => { stop(e); setZoom((z) => Math.min(4, +(z + 0.5).toFixed(2))); }}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur cursor-pointer"
          aria-label="Zoom in"
          data-testid="lightbox-zoom-in"
        >
          <ZoomIn className="w-4 h-4" strokeWidth={1.5} />
        </button>
        {zoom > 1 && (
          <span className="text-white/80 text-xs tabular-nums px-2">{Math.round(zoom * 100)}%</span>
        )}
      </div>

      {/* Counter */}
      {total > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs backdrop-blur tabular-nums" data-testid="lightbox-counter">
          {safeIndex + 1} / {total}
        </div>
      )}

      {/* Prev / Next */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { stop(e); prev(); }}
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center backdrop-blur cursor-pointer"
            aria-label="Precedente"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={(e) => { stop(e); next(); }}
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center backdrop-blur cursor-pointer"
            aria-label="Successiva"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </>
      )}

      {/* Delete (optional) */}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { stop(e); onDelete(safeIndex); if (total <= 1) handleClose(); }}
          className="absolute bottom-4 right-4 z-50 inline-flex items-center gap-1.5 px-3 h-10 rounded-full bg-red-600/80 hover:bg-red-600 text-white text-xs backdrop-blur cursor-pointer"
          data-testid="lightbox-delete"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Elimina foto
        </button>
      )}
    </div>
  );
}
